import { useRouter } from "next/router";

import { useCallback, useMemo, useState } from "react";

import { twMerge } from "tailwind-merge";

import { faSpinner } from "@fortawesome/free-solid-svg-icons/faSpinner";

import CustomCard from "components/CustomCard";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import useNotification from "components/Notification/useNotification";
import ProfileSnippet from "components/ProfileSnippet";
import useActivityTracker from "hooks/useActivityTracker";
import { useApi } from "hooks/useApi";
import useAuthentication from "hooks/useAuthentication";
import { ApiEndpoint, baseFetcher, fetchApi } from "utils/baseFetcher";

type CollectionPageRelatedIPProps = {
  collection: any;
  card?: boolean;
  widget?: boolean;
};

export const collectionRelatedIPUrl = (slug: string, pageIndex: number) => {
  const url = new ApiEndpoint(
    `/api/v2/collections/${slug}/related-ip-accounts`
  );
  url.searchParams.append("include", "company");
  url.searchParams.append("filter[relation-type]", "wrote-insights");
  url.searchParams.append("page[size]", "12");
  url.searchParams.append("page[number]", `${pageIndex}`);
  url.searchParams.append("sort", "-ip-rank");

  return url.href;
};

const CollectionPageRelatedIP = ({
  collection,
  widget,
}: CollectionPageRelatedIPProps) => {
  const router = useRouter();
  const slug = router.query.slug as string;

  const { authenticated, account } = useAuthentication();
  const { setNotification } = useNotification();
  const { trackActivity } = useActivityTracker();

  const { data: accounts, mutate } = useApi(
    collectionRelatedIPUrl(slug, 1),
    baseFetcher
  );

  const [following, setFollowing] = useState(false);

  const followedAll = useMemo(() => {
    return !accounts?.data?.some?.((a: any) => !a.isFollowed);
  }, [accounts?.data]);

  const followAll = useCallback(() => {
    const notFollowed = accounts?.data?.filter?.((a: any) => !a.isFollowed);
    if (!notFollowed.length) {
      mutate();
      return;
    }
    const data = {
      data: {
        id: account?.id,
        type: "accounts",
        relationships: {
          "following-accounts": {
            data: notFollowed.map(({ id }: any) => {
              return {
                id,
                type: "accounts",
              };
            }),
          },
        },
      },
    };

    setFollowing(true);
    fetchApi(`/api/v2/accounts/${account?.id}/follow-accounts`, {
      method: "PATCH",
      headers: { "content-type": "application/vnd.api+json" },
      body: JSON.stringify(data),
    })
      .then(() => {
        setFollowing(false);
        setNotification("All analysts have been followed.", "success");
        mutate();
        trackActivity("collection.follow_all_ips", collection);
      })
      .catch(() => {
        setFollowing(false);
        setNotification("Something went wrong!", "error");
      });
  }, [
    account?.id,
    accounts?.data,
    collection,
    mutate,
    setNotification,
    trackActivity,
  ]);

  const onFollow = useCallback(
    (profile) => {
      mutate({
        ...accounts,
        data: accounts.data.map((a: any) => {
          if (profile.id === a.id) {
            return {
              ...a,
              isFollowed: profile?.isFollowed,
            };
          }

          return a;
        }),
      });
    },
    [accounts, mutate]
  );

  if (!accounts?.data?.length) return <></>;

  const content = (
    <div>
      <div
        className={twMerge(
          "px-[15px]",
          "py-[10px]",
          "font-medium",
          "text-[16px] leading-[26px]",
          "text-[#1f1f20]"
        )}
      >
        Related Providers
        {authenticated && !followedAll && (
          <button
            className={twMerge(
              "rounded-full px-[1em] py-[0.75em] leading-[1] min-w-[6em]",
              "text-[11px]",
              "bg-smart-teal border-none text-white font-medium hover:bg-[#1d8988]",
              "float-right"
            )}
            onClick={followAll}
          >
            {following && (
              <FontAwesomeIcon
                icon={faSpinner}
                className="animate-spin mr-[5px]"
              />
            )}
            Follow All
          </button>
        )}
      </div>
      <div className="overflow-y-auto h-[230px]">
        {accounts?.data?.map?.((profile: any) => (
          <ProfileSnippet
            key={profile.id + profile.isFollowed}
            profile={profile}
            showFollowButton
            showCompany
            onFollow={onFollow}
          />
        ))}
      </div>
    </div>
  );

  if (widget) {
    return <CustomCard className={"p-0"}>{content}</CustomCard>;
  }

  return content;
};

export default CollectionPageRelatedIP;
